import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import GameMenuButton from "@/components/GameMenuButton";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO =
  "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/5dx73wcq_IMG_8068.JPG";

const BG =
  "https://customer-assets.emergentagent.com/job_glow-arcade-1/artifacts/zfce4w4g_IMG_8341.JPG";

const ComingSoon = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const mainRef = useRef(null);

  // Retro progress bar: loads in a pseudo-organic way, never completes
  useEffect(() => {
    let frame;
    let target = 0;
    let current = 0;
    const tick = () => {
      target = Math.min(87, target + Math.random() * 1.2);
      current += (target - current) * 0.08;
      setProgress(current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Your name and email. Nothing else.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/early-access`, {
        name: name.trim(),
        email: email.trim(),
      });
      setDone(true);
      toast.success("You're on the list. Stand by.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main
      ref={mainRef}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0510] text-white flex flex-col"
      data-testid="coming-soon"
    >
      {/* Background layers */}
      <div
        className="absolute inset-0 opacity-[0.28]"
        style={{
          backgroundImage: `url('${BG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0 hero-ambient"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 25% 35%, rgba(176,38,255,0.45), transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(255,0,122,0.35), transparent 55%), radial-gradient(ellipse at 55% 15%, rgba(0,240,255,0.3), transparent 60%)",
          backgroundSize: "200% 200%, 200% 200%, 200% 200%",
          opacity: 0.35,
        }}
      />
      <div className="absolute inset-0 grid-bg opacity-30 radial-fade pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,5,16,0.65) 0%, rgba(10,5,16,0.25) 40%, rgba(10,5,16,0.9) 100%)",
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <img
            src={LOGO}
            alt="Replay Arcade & Lounge"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-white/10 shadow-[0_0_18px_rgba(176,38,255,0.55)]"
          />
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.4em] text-white/80">
            Arcade <span className="text-[#FF007A]">&amp;</span> Lounge <span className="text-white/30">◉</span> Strabane
          </span>
        </div>
      </header>

      {/* Center */}
      <section className="relative z-10 flex-1 flex items-center justify-center px-6 md:px-12 py-6 sm:py-10">
        <div className="max-w-3xl w-full text-center flex flex-col items-center">
          <Reveal>
            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10 mb-6 font-mono text-[10px] sm:text-xs uppercase tracking-[0.4em]"
              data-testid="cs-menu-indicator"
            >
              <span className="inline-flex items-center gap-3 text-[#00F0FF]">
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full bg-[#00F0FF] shadow-[0_0_10px_#00F0FF] calm-breath"
                />
                New Game
              </span>
              <span aria-hidden="true" className="h-px w-10 bg-white/20 hidden sm:block" />
              <span className="inline-flex items-center gap-3 text-[#FF007A]">
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full bg-[#FF007A] shadow-[0_0_10px_#FF007A] calm-breath"
                  style={{ animationDelay: "2.25s" }}
                />
                Continue
              </span>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="relative mb-5 sm:mb-7">
              <div
                aria-hidden="true"
                className="absolute inset-0 -m-10 rounded-full bg-[radial-gradient(ellipse,_rgba(176,38,255,0.5),_transparent_60%)] blur-2xl"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 -m-6 rounded-full bg-[radial-gradient(ellipse,_rgba(0,240,255,0.3),_transparent_60%)] blur-xl"
              />
              <img
                src={LOGO}
                alt="Replay Arcade & Lounge"
                className="relative h-28 w-28 sm:h-36 sm:w-36 md:h-44 md:w-44 rounded-full object-cover ring-1 ring-white/15 shadow-[0_0_60px_rgba(176,38,255,0.6),0_0_120px_rgba(0,240,255,0.25)] slow-spin"
                data-testid="coming-soon-logo"
              />
            </div>
          </Reveal>

          <Reveal delay={180}>
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] calm-breath">
              ◉ Opening Soon
            </p>
          </Reveal>

          <Reveal delay={260}>
            <h1 className="mt-4 font-display font-black text-5xl sm:text-7xl md:text-[5.5rem] leading-[0.92] tracking-tighter hero-glow text-white">
              New Memories
              <br />
              <span className="neon-pink">Loading</span>
              <span className="neon-cyan">...</span>
            </h1>
          </Reveal>

          <Reveal delay={400}>
            <p className="mt-6 max-w-md mx-auto text-base sm:text-lg text-[#C9C0D6] leading-relaxed font-body">
              You don&rsquo;t find this place.
              <br />
              <span className="text-white">You step into it.</span>
            </p>
          </Reveal>

          {/* Retro progress bar */}
          <Reveal delay={520}>
            <div className="mt-7 w-full max-w-md mx-auto" data-testid="coming-soon-progress">
              <div className="flex items-center justify-between mb-2 font-mono text-[10px] uppercase tracking-[0.4em] text-white/55">
                <span>Loading experience</span>
                <span className="text-[#00F0FF]">{Math.floor(progress)}%</span>
              </div>
              <div className="h-1.5 w-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#00F0FF] via-[#B026FF] to-[#FF007A] shadow-[0_0_10px_rgba(255,0,122,0.6)]"
                  style={{ width: `${progress}%`, transition: "width 200ms linear" }}
                />
              </div>
              <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.4em] text-white/35">
                Please stand by. The arcade is almost awake.
              </p>
            </div>
          </Reveal>

          {/* Waitlist CTA — opens a minimal dialog */}
          <Reveal delay={640}>
            <div className="mt-7 w-full max-w-xl flex justify-center" data-testid="coming-soon-cta">
              <GameMenuButton
                type="button"
                variant="cyan"
                hint="Sign up for early access"
                data-testid="cs-open-dialog"
                onClick={() => setDialogOpen(true)}
              >
                Join the Waitlist
              </GameMenuButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center sm:justify-between gap-4">
          <div className="inline-flex items-center gap-3 border border-white/10 px-3.5 py-2">
            <MapPin className="h-3.5 w-3.5 text-[#FF007A]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/70">
              Abercorn Square ◉ Strabane
            </span>
          </div>
          <p
            className="font-display text-base sm:text-xl font-black tracking-tight neon-pink calm-breath"
            data-testid="cs-signature"
          >
            Press Start on Real Life
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/35">
            © {new Date().getFullYear()} Replay
          </p>
        </div>
      </footer>

      {/* Waitlist dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) {
            setTimeout(() => {
              setDone(false);
              setName("");
              setEmail("");
            }, 280);
          }
        }}
      >
        <DialogContent
          data-testid="cs-dialog"
          className="max-w-md border border-[#FF007A]/40 bg-[#0A0510]/95 backdrop-blur-2xl text-white p-0 overflow-hidden rounded-none shadow-[0_0_60px_rgba(255,0,122,0.25)]"
        >
          <div className="relative">
            <div className="absolute inset-0 pointer-events-none crt-scanlines opacity-25" />
            <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF007A] to-transparent" />
            <div className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent" />

            <div className="relative px-7 sm:px-9 py-8 sm:py-10">
              {!done ? (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] flicker-slow">
                    ◉ Join the waitlist
                  </p>
                  <DialogTitle asChild>
                    <h3 className="font-display mt-3 text-3xl sm:text-4xl font-black neon-pink leading-[1.05]">
                      Be the first in.
                    </h3>
                  </DialogTitle>
                  <DialogDescription asChild>
                    <p className="mt-3 max-w-sm text-sm sm:text-base text-[#C9C0D6] leading-relaxed">
                      Drop your name and email.
                      <br />
                      We&rsquo;ll be in touch.
                    </p>
                  </DialogDescription>

                  <form
                    onSubmit={handleSubmit}
                    className="mt-7 flex flex-col gap-5"
                    data-testid="cs-form"
                  >
                    <label className="flex flex-col gap-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55">
                        Your name
                      </span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Who's stepping in"
                        data-testid="cs-name"
                        autoComplete="name"
                        className="bg-transparent border-0 border-b border-white/15 px-0 py-2 text-base text-white placeholder:text-white/30 focus:outline-none focus:border-[#00F0FF] transition-all"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55">
                        Email
                      </span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@somewhere.io"
                        data-testid="cs-email"
                        autoComplete="email"
                        className="bg-transparent border-0 border-b border-white/15 px-0 py-2 text-base text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF007A] transition-all"
                      />
                    </label>

                    <div className="mt-3 flex justify-center">
                      <GameMenuButton
                        type="submit"
                        variant="cyan"
                        hint={submitting ? "Saving..." : "Request early access"}
                        data-testid="cs-submit"
                        disabled={submitting}
                      >
                        {submitting ? "Standby..." : "Join the Waitlist"}
                      </GameMenuButton>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center" data-testid="cs-success">
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center border border-[#00F0FF] text-[#00F0FF] flicker-slow">
                    <span className="font-mono text-lg">◉</span>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
                    ◉ Signal received
                  </p>
                  <h3 className="mt-3 font-display text-4xl sm:text-5xl font-black neon-pink leading-none">
                    You&rsquo;re in.
                  </h3>
                  <p className="mt-5 text-[#C9C0D6] leading-relaxed max-w-sm mx-auto">
                    We&rsquo;ll be in touch soon.
                  </p>
                  <p className="mt-2 text-white leading-relaxed max-w-sm mx-auto">
                    You&rsquo;re one step closer to stepping inside{" "}
                    <span className="neon-cyan">Replay</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default ComingSoon;
